/**
 * NotFoundPage.jsx
 * صفحه‌ی سراسری «۴۰۴ — پیدا نشد» — وقتی مسیری در React Router با هیچ Route
 * مطابقت نداشته باشد (نگاه کنید به Route path="*" در App.jsx) اینجا نمایش
 * داده می‌شود. طراحی هم‌راستا با فضای داستانی قطار/سفر (journey.css) است تا
 * حتی خطای «صفحه پیدا نشد» هم در دنیای پاترهد بماند. دوزبانه است: دامنه‌ی
 * potterhead.ir فارسی و بقیه (از جمله potterheads.org) انگلیسی نمایش داده
 * می‌شوند — همان قرارداد IS_PERSIAN که در LandingPage/AboutPage/ContactPage
 * استفاده شده.
 *
 * تعداد ذرات تزئینی (ستاره/کرم‌شب‌تاب/جرقه) بسته به عرض صفحه کم‌وزیاد
 * می‌شود تا هم روی موبایل سبک بماند و هم prefers-reduced-motion را با CSS
 * (notfound.css) رعایت کند.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/journey.css';
import '../styles/notfound.css';

const hostname = window.location.hostname;
const IS_PERSIAN = hostname === 'potterhead.ir' || hostname === 'www.potterhead.ir';

const T = {
  fa: {
    dir: 'rtl',
    overline: 'طلسم گمشده',
    title404: '۴۰۴',
    heading: 'این راهرو روی نقشه‌ی هاگوارتز نیست',
    subtitle:
      'به‌نظر می‌رسد پله‌های متحرک هاگوارتز دوباره جابه‌جا شده‌اند، یا شاید پیوز این صفحه را قایم کرده! نشانی‌ای که دنبالش بودی یا هرگز وجود نداشته یا به‌جای دیگری منتقل شده.',
    quote: '"شرارت انجام شد" — اما صفحه‌ای که دنبالش بودی پیدا نشد.',
    home: '🏰 بازگشت به تالار بزرگ',
    contact: '🦉 ارتباط با پشتیبانی',
    mapOpen: '🗺️ نقشه‌ی دزدان را باز کن',
    mapClose: 'بستن نقشه',
    mapLine1: 'من قسم می‌خورم که قصد شرارت دارم…',
    mapLine2: 'شرارت انجام شد.',
    backHome: 'بازگشت به صفحه اصلی',
  },
  en: {
    dir: 'ltr',
    overline: 'Lost Spell',
    title404: '404',
    heading: "This Corridor Isn't on the Map",
    subtitle:
      "It seems the Hogwarts staircases rearranged themselves again, or perhaps Peeves hid this page. The address you're looking for either never existed or has moved elsewhere.",
    quote: '"Mischief Managed" — but the page you sought was not found.',
    home: '🏰 Return to the Great Hall',
    contact: '🦉 Contact Support',
    mapOpen: "🗺️ Open the Marauder's Map",
    mapClose: 'Close the map',
    mapLine1: 'I solemnly swear that I am up to no good…',
    mapLine2: 'Mischief managed.',
    backHome: 'Back to the homepage',
  },
};

const t = IS_PERSIAN ? T.fa : T.en;

// عرض صفحه هنگام mount — برای تنظیم تعداد ذرات تزئینی (کارایی روی موبایل)
const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
const isSmallViewport = viewportWidth < 480;
const isMediumViewport = viewportWidth < 900;

function rand(min, max) {
  return Math.random() * (max - min) + min;
}

function Stars() {
  const count = isSmallViewport ? 45 : isMediumViewport ? 65 : 90;
  const stars = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        top: rand(0, 100),
        left: rand(0, 100),
        size: rand(0.5, 3),
        dur: rand(2, 5),
        delay: rand(0, 4),
      })),
    [count]
  );
  return (
    <div className="stars-bg" aria-hidden>
      {stars.map((s) => (
        <div
          key={s.id}
          className="star"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            '--dur': `${s.dur}s`,
            '--delay': `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function Fireflies() {
  const count = isSmallViewport ? 6 : isMediumViewport ? 10 : 16;
  const flies = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: rand(4, 96),
        size: rand(3, 6),
        dur: rand(9, 18),
        delay: rand(0, 14),
        drift: rand(-40, 40),
      })),
    [count]
  );
  return (
    <div className="nf-fireflies" aria-hidden>
      {flies.map((f) => (
        <span
          key={f.id}
          className="nf-firefly"
          style={{
            left: `${f.left}%`,
            '--fs': `${f.size}px`,
            '--dur': `${f.dur}s`,
            '--delay': `${f.delay}s`,
            '--drift': `${f.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function Candles() {
  const count = isSmallViewport ? 4 : 7;
  const candles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: 6 + i * (88 / Math.max(count - 1, 1)) + rand(-2, 2),
        top: rand(0, 12),
        delay: rand(0, 3),
        dur: rand(5, 8),
      })),
    [count]
  );
  return (
    <div className="nf-candles" aria-hidden>
      {candles.map((c) => (
        <div
          key={c.id}
          className="nf-candle"
          style={{ left: `${c.left}%`, top: `${c.top}%`, animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }}
        />
      ))}
    </div>
  );
}

function Sparkles() {
  const sparkles = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        id: i,
        top: rand(-15, 105),
        left: rand(-8, 108),
        size: rand(2, 4),
        dur: rand(1.8, 3.2),
        delay: rand(0, 3),
      })),
    []
  );
  return (
    <>
      {sparkles.map((s) => (
        <span
          key={s.id}
          className="nf-sparkle"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            '--dur': `${s.dur}s`,
            '--delay': `${s.delay}s`,
          }}
        />
      ))}
    </>
  );
}

// شکل ساده‌ی خط آسمان هاگوارتز — چند برج با ارتفاع/عرض متفاوت به‌همراه
// چند پنجره‌ی روشن که چشمک می‌زنند. کاملاً تزئینی و فقط با CSS/SVG.
function CastleSilhouette() {
  const towers = useMemo(
    () => [
      { x: -20, w: 70, h: 70, roof: 30 },
      { x: 55, w: 90, h: 110, roof: 40 },
      { x: 150, w: 60, h: 60, roof: 26 },
      { x: 220, w: 110, h: 150, roof: 55 },
      { x: 340, w: 70, h: 90, roof: 34 },
      { x: 420, w: 130, h: 190, roof: 65 },
      { x: 560, w: 80, h: 100, roof: 38 },
      { x: 650, w: 60, h: 65, roof: 28 },
      { x: 720, w: 100, h: 130, roof: 48 },
      { x: 830, w: 75, h: 80, roof: 32 },
      { x: 915, w: 110, h: 155, roof: 56 },
      { x: 1035, w: 65, h: 70, roof: 28 },
      { x: 1110, w: 90, h: 100, roof: 38 },
    ],
    []
  );
  const baseline = 300;
  return (
    <div className="nf-castle" aria-hidden>
      <svg viewBox="0 0 1200 300" preserveAspectRatio="xMidYMax slice">
        <rect x="-30" y={baseline - 25} width="1260" height="25" fill="#050508" />
        {towers.map((tw, i) => {
          const topY = baseline - tw.h;
          const roofTipY = topY - tw.roof;
          const midX = tw.x + tw.w / 2;
          const winCount = tw.h > 100 ? 3 : tw.h > 70 ? 2 : 1;
          return (
            <g key={i}>
              <rect x={tw.x} y={topY} width={tw.w} height={tw.h} fill="#0c0c14" stroke="#1a1a2e" strokeWidth="1" />
              <polygon
                points={`${tw.x - 4},${topY} ${midX},${roofTipY} ${tw.x + tw.w + 4},${topY}`}
                fill="#08080f"
              />
              {Array.from({ length: winCount }).map((_, wi) => (
                <rect
                  key={wi}
                  className="nf-castle-window"
                  x={midX - 3}
                  y={topY + 14 + wi * ((tw.h - 24) / Math.max(winCount, 1))}
                  width="6"
                  height="9"
                  rx="2"
                  fill="#f0d080"
                  style={{ '--delay': `${((i * 3 + wi) % 7) * 0.45}s` }}
                />
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// حلقه‌ی جادویی چرخان پشت کارت — کاملاً تزئینی، اندازه‌اش با vmin خودش را
// با هر صفحه‌نمایشی تطبیق می‌دهد.
function MagicRing() {
  return (
    <svg className="nf-ring" viewBox="0 0 200 200" aria-hidden>
      <circle cx="100" cy="100" r="92" fill="none" stroke="#c9a84c" strokeWidth="0.6" strokeDasharray="2 6" />
      <circle cx="100" cy="100" r="78" fill="none" stroke="#c9a84c" strokeWidth="0.5" opacity="0.6" />
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const x1 = 100 + Math.cos(angle) * 78;
        const y1 = 100 + Math.sin(angle) * 78;
        const x2 = 100 + Math.cos(angle) * 86;
        const y2 = 100 + Math.sin(angle) * 86;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f0d080" strokeWidth="0.6" />;
      })}
      <circle cx="100" cy="100" r="60" fill="none" stroke="#f0d080" strokeWidth="0.4" opacity="0.5" />
    </svg>
  );
}

export default function NotFoundPage() {
  const [mapOpen, setMapOpen] = useState(false);

  return (
    <div className="journey-root" dir={t.dir}>
      <Stars />
      <Fireflies />
      <CastleSilhouette />
      <Candles />
      <div className="nf-ghost" aria-hidden>👻</div>

      <div className="nf-wrap">
        <MagicRing />

        <div className="nf-card">
          <span className="nf-overline">{t.overline}</span>

          <div className="nf-404-wrap">
            <Sparkles />
            <div className="nf-404 cinzel">{t.title404}</div>
          </div>

          <div className="nf-house-ribbon" aria-hidden>🦁 🦡 🦅 🐍</div>

          <h1 className="nf-title cinzel">{t.heading}</h1>

          <p className="nf-subtitle">{t.subtitle}</p>

          <div className="nf-stairs" aria-hidden>
            <div className="nf-stair" />
            <div className="nf-stair" />
            <div className="nf-stair" />
            <div className="nf-stair" />
            <div className="nf-stair" />
            <div className="nf-stair" />
          </div>

          <span className="nf-quote">{t.quote}</span>

          <div className="nf-actions">
            <Link to="/" className="nf-btn-primary">
              {t.home}
            </Link>
            <Link to="/contact" className="nf-btn-secondary">
              {t.contact}
            </Link>
          </div>

          <button
            type="button"
            className="nf-map-toggle"
            onClick={() => setMapOpen((v) => !v)}
            aria-expanded={mapOpen}
          >
            {mapOpen ? t.mapClose : t.mapOpen}
          </button>

          <div className={`nf-map-panel ${mapOpen ? 'nf-open' : 'nf-closed'}`}>
            {mapOpen && (
              <>
                <div className="nf-map-line cinzel">{t.mapLine1}</div>
                <div className="nf-map-line cinzel nf-line-2">{t.mapLine2}</div>
                <div className="nf-footprints" aria-hidden>
                  <span>👣</span>
                  <span>👣</span>
                  <span>👣</span>
                  <span>👣</span>
                  <span>👣</span>
                </div>
              </>
            )}
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/" className="nf-home-link">
              {t.backHome}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
